import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Share } from "lucide-react";
import { useTranslation } from 'react-i18next';
import Button from "../../../Shared/Buttons/Button";

const useStyles = makeStyles((theme) => ({
  sharingContainer: {
    position: "relative",
    zIndex: "600",
  },

  sharingDialog: {
    width: "320px",
    maxWidth: "90vw",
    height: "fit-content",
    position: "absolute",
    zIndex: "998",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "24px",
    backgroundColor: "white",
    border: "1px solid #EDEDED",
    borderRadius: "8px",
    top: "48px",
    right: "0px",
    padding: "16px",
  },

  sharingDialog__subtitle: {
    textAlign: "center",
    color: "rgba(12, 14, 41, 0.6)",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
  },

  sharingDialog__urlRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  sharingDialog__copyWrapper: {
    position: "relative",
    flexShrink: 0,
  },

  sharingDialog__copiedBadge: {
    position: "absolute",
    bottom: "calc(100% + 8px)",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#1a1a2e",
    color: "#ffffff",
    fontSize: "11px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "600",
    letterSpacing: "0.04em",
    padding: "5px 10px",
    borderRadius: "20px",
    whiteSpace: "nowrap",
    pointerEvents: "none",
    boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
    animation: "$copiedFadeUp 0.18s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
  },

  "@keyframes copiedFadeUp": {
    from: { opacity: 0, transform: "translateX(-50%) translateY(6px) scale(0.9)" },
    to: { opacity: 1, transform: "translateX(-50%) translateY(0) scale(1)" },
  },

  sharingDialog__urlInput: {
    flex: 1,
    height: "32px",
    padding: "0 8px",
    border: "1px solid #EDEDED",
    borderRadius: "4px",
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "rgba(12, 14, 41, 0.7)",
    backgroundColor: "#F9F9F9",
    outline: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },

  sharingDialog__disableButton: {
    alignSelf: "center",
    background: "none",
    border: "none",
    padding: "0",
    fontSize: "13px",
    color: "rgba(12, 14, 41, 0.5)",
    textDecoration: "underline",
    cursor: "pointer",
    userSelect: "none",

    "&:hover:not(:disabled)": {
      color: "rgba(12, 14, 41, 0.8)",
    },

    "&:disabled": {
      opacity: 0.5,
      cursor: "default",
    },
  },
}));

/**
 * Self-contained sharing button + dropdown for graph pages.
 *
 * Props:
 *   isPublished  — boolean: whether the graph is currently published
 *   shareUrl     — string:  the view URL to copy to clipboard
 *   onEnable     — fn:      called when the user clicks "Enable sharing"
 *   onDisable    — fn:      called when the user clicks "Disable sharing"
 *   loading      — boolean: disables actions while an API call is in flight
 */
export default function ShareGraphDropdown({ isPublished, shareUrl, onEnable, onDisable, loading }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const buttonRef = useRef(null);
  const dialogRef = useRef(null);

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        setCopiedToClipboard(true);
        setTimeout(() => setCopiedToClipboard(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
      });
  };

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dialogRef.current &&
        !dialogRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div className={classes.sharingContainer}>
      <span ref={buttonRef}>
        <Button
          variant="outlined"
          startIcon={<Share absoluteStrokeWidth />}
          onClick={() => setOpen((prev) => !prev)}
        >
          {t('shareGraph.sharing')}
        </Button>
      </span>

      {open && (
        <div className={classes.sharingDialog} ref={dialogRef}>
          {isPublished ? (
            <>
              <div className={classes.sharingDialog__urlRow}>
                <input
                  className={classes.sharingDialog__urlInput}
                  type="text"
                  readOnly
                  value={shareUrl}
                  onFocus={(e) => e.target.select()}
                />
                <div className={classes.sharingDialog__copyWrapper}>
                  {copiedToClipboard && (
                    <span className={classes.sharingDialog__copiedBadge}>{t('common.copied')}</span>
                  )}
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleCopyLink}
                    disabled={copiedToClipboard || loading}
                  >
                    {t('shareGraph.copyLink')}
                  </Button>
                </div>
              </div>
              <p className={classes.sharingDialog__subtitle}>
                {t('shareGraph.anyoneWithLink')}
              </p>
              <button
                className={classes.sharingDialog__disableButton}
                onClick={onDisable}
                disabled={loading}
              >
                {loading ? t('shareGraph.disabling') : t('shareGraph.disableSharing')}
              </button>
            </>
          ) : (
            <>
              <Button
                style={{ width: "100%" }}
                onClick={onEnable}
                disabled={loading}
              >
                {loading ? t('shareGraph.enabling') : t('shareGraph.enableSharing')}
              </Button>
              <p className={classes.sharingDialog__subtitle}>
                {t('shareGraph.anyoneWithLink')}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
